import { IObservableArray, makeAutoObservable, observable } from 'mobx'
import { createContext, FC, useContext, useMemo } from 'react'
import { ItemId, moveItemOnTree, TreeData, TreeDestinationPosition, TreeItem, TreeSourcePosition } from 'tree-dnd'

import { ParentClientProp, TreeDataItem, TreeItemType } from '../types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { reshuffle } from '../treeUtils'
import { isUndefined } from 'lodash'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'

const isTmpId = (id: any) => id < 0

export class FilesTreeStore {
  rootId: 0 = 0
  items = observable.map<ItemId, TreeItem>({})
  draggingId: ItemId | null = null

  constructor() {
    makeAutoObservable(this)
  }

  setTree = (tree: TreeData) => {
    Object.entries(tree.items).forEach(([id, item]) => this.items.set(id, item))
  }

  onExpand = (itemId: ItemId) => {
    const item = this.items.get(itemId)
    if (!item) return
    item.isExpanded = true
  }

  onCollapse = (itemId: ItemId) => {
    const item = this.items.get(itemId)
    if (!item) return

    item.isExpanded = false
  }

  onDragStart = (itemId: ItemId) => {
    this.draggingId = itemId
  }

  get tree(): TreeData {
    return {
      rootId: this.rootId,
      items: Object.fromEntries(this.items),
    }
  }

  onDragEnd = (
    source: TreeSourcePosition,
    destination?: TreeDestinationPosition,
    { onDragDone }: { onDragDone?: (draggingId: string, destinationIndex: number) => Promise<void> } = {},
  ) => {
    if (
      !destination ||
      !this.draggingId ||
      source.parentId !== destination?.parentId ||
      source.parentId !== '0' ||
      destination?.parentId !== '0' ||
      isUndefined(destination?.index) ||
      destination.index === source.index
    )
      return

    const oldTree = this.tree

    const newTree = moveItemOnTree(this.tree, source, destination)

    const destinationPosition = reshuffle(newTree, source, destination, this.draggingId)

    if (!isUndefined(destinationPosition)) {
      onDragDone?.(this.draggingId as string, destinationPosition)?.catch((error) => {
        reportError(error)
        showErrorMessageToast('Something went wrong, our team has been notified.')
        this.setTree(oldTree)
      })
      this.setTree(newTree)
      this.draggingId = null
    }
  }

  getItemParent = (itemId: ItemId) => {
    const item = this.items.get(itemId)
    if (!item) return

    if (!item.data?.parent_id) {
      return this.items.get(this.rootId.toString())
    }

    return this.items.get(item.data.parent_id)
  }

  appendChildren = (parentId: ItemId, childFiles: TreeDataItem[], parentClient: ParentClientProp) => {
    if (!this.tree) return

    const expandedTreeItem = this.items.get(parentId)

    if (childFiles && expandedTreeItem) {
      const newChild = expandedTreeItem.children.filter((childId) => isTmpId(childId))
      ;(expandedTreeItem.children as IObservableArray<ItemId>).replace(newChild)

      childFiles.forEach((file: TreeDataItem) => {
        if (!this.tree) return
        const fileId = file.mention_id ? `${file.id}-${file.mention_id}` : file.id

        const existingItem = this.items.get(fileId)
        const isChangedParent = !!existingItem?.data && existingItem?.data?.parent_id !== parentId

        if (existingItem) {
          const previousParent = this.getItemParent(existingItem.id)
          if (isChangedParent && previousParent) {
            ;(previousParent.children as IObservableArray<ItemId>).remove(existingItem.id)
          }

          this.items.set(fileId, {
            id: fileId.toString(),
            children: existingItem.children,
            hasChildren: existingItem.children.length > 0,
            isExpanded: existingItem.isExpanded,
            data: { ...file, parentId: parentId, parentClient: parentClient },
          })
        } else {
          this.items.set(fileId, {
            id: fileId.toString(),
            children: [],
            hasChildren: false,
            isExpanded: false,
            data: { ...file, parentId: parentId, parentClient: parentClient },
          })
        }

        expandedTreeItem.children.push(fileId)
      })

      expandedTreeItem.isExpanded = true
      expandedTreeItem.hasChildren = !!childFiles.length
    }
  }

  isLast = (itemId: ItemId) => {
    if (!this.tree) return false

    const item = this.items.get(itemId)
    if (!item) return false

    const parent = this.getItemParent(item.id.toString())
    if (!parent || parent.children.length < 1) return false

    const lastChild = parent.children[parent.children.length - 1]
    return !!lastChild && lastChild.toString() === itemId
  }

  removeItem = (itemId: ItemId) => {
    if (!this.tree) return

    const item = this.items.get(itemId)

    if (item) {
      const parent = this.getItemParent(item.id.toString())
      if (!parent) return
      this.items.delete(itemId)
      parent.children = parent.children.filter((childId) => childId.toString() !== itemId)
      parent.hasChildren = parent.children.length > 0
    }
  }

  updateItem = (itemId: ItemId, data: TreeDataItem) => {
    if (!this.tree) return

    const item = this.items.get(itemId)
    if (!item) return

    if (isTmpId(itemId)) {
      const parent = this.getItemParent(item.id)
      if (!parent) return

      item.data = data
      this.items.delete(itemId)
      parent.children.unshift(data.id)
      ;(parent.children as IObservableArray<ItemId>).remove(itemId)
      this.items.set(data.id, {
        id: data.id,
        children: [],
        hasChildren: false,
        isExpanded: false,
        data: data,
      })
    } else {
      this.items.set(itemId, {
        ...item,
        data: data,
      })
    }
  }

  setItemType = (itemId: ItemId, type: TreeItemType) => {
    if (!this.tree) return

    const item = this.items.get(itemId)
    if (!item) return

    item.data.type = type
  }

  get isEmpty() {
    return !this.tree || this.items.size <= 1
  }

  get isLoaded() {
    return !this.isEmpty
  }
}

export const FilesTreeContext = createContext<FilesTreeStore | null>(null)

export const useFilesTreeStore = () => {
  const store = useContext(FilesTreeContext)

  if (!store) {
    throw new Error('FilesTreeContext should be invoked within a FilesTreeContext.Provider')
  }

  return store
}

export const FilesTreeProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const workspaceId = useCurrentWorkspaceId()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const store = useMemo(() => new FilesTreeStore(), [workspaceId])

  return <FilesTreeContext.Provider value={store}>{children}</FilesTreeContext.Provider>
}
